const React = require("react");
const { useCJDropshipping } = require("../context/CJDropshippingContext");
const { useDropshippingResource } = require("../hooks/useDropshippingResource");
const { PageHeader, Button, ResourceState, SupplierCard, PaginationBar } = require("../components/ui");
const { CJDropshippingViewModal } = require("../components/CJDropshippingViewModal");
const { ShipFromFilter } = require("../components/ShipFromFilter");
const { DefaultLoadingManager } = require("../components/DefaultLoadingManager");
const h = React.createElement;

const PAGE_SIZE = 24;

const DropshippingSuppliers = () => {
  const { api, t } = useCJDropshipping();
  const [page, setPage] = React.useState(1);
  const [forceRefresh, setForceRefresh] = React.useState(0);
  const [viewSupplierId, setViewSupplierId] = React.useState(null);
  const [keyword, setKeyword] = React.useState("");
  const [query, setQuery] = React.useState("");
  const [fromCountryCode, setFromCountryCode] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [mutationError, setMutationError] = React.useState("");
  const [busySupplierId, setBusySupplierId] = React.useState("");
  const [showDefaultManager, setShowDefaultManager] = React.useState(false);

  const resource = useDropshippingResource(
    () => api.listSuppliers({ page, limit: PAGE_SIZE, keyword: query, fromCountryCode, ...(forceRefresh ? { refresh: "true" } : {}) }),
    [api, page, query, fromCountryCode, forceRefresh]
  );
  const suppliers = resource.data?.suppliers || [];
  const personalResource = useDropshippingResource(
    () => api.listPersonalSuppliers({ name: query }),
    [api, query]
  );
  const personalSuppliers = personalResource.data?.suppliers || [];
  const total = resource.data?.total ?? suppliers.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  React.useEffect(() => { setPage(1); }, [query, fromCountryCode]);
  React.useEffect(() => {
    const timer = setTimeout(() => setQuery(keyword.trim()), 300);
    return () => clearTimeout(timer);
  }, [keyword]);

  // "Sync all suppliers" : ré-exécute l'agrégation CJ synthétique et upsert
  // chaque fournisseur (même chemin que le refresh=true déjà en place),
  // simplement renommé pour ne pas cohabiter avec le bouton "Sync" par card
  // sous deux libellés différents pour un concept identique.
  const syncAllSuppliers = () => setForceRefresh((value) => value + 1);

  const syncOne = async (supplier) => {
    setBusySupplierId(supplier._id);
    setMutationError("");
    setMessage("");
    try {
      const result = await api.syncSupplier(supplier._id);
      setMessage(result.warning || t("h_cj_sync_success", "Product synchronized."));
      await resource.reload();
    } catch (error) {
      setMutationError(error.message);
    } finally {
      setBusySupplierId("");
    }
  };

  return h(React.Fragment, null,
    h(PageHeader, {
      title: t("h_cj_suppliers_title", "Suppliers"),
      description: t("h_cj_suppliers_desc", "Manage your private Elakay suppliers and browse suppliers exposed by your connected CJ account."),
      action: h("div", { className: "cjds-row" },
        h(Button, { onClick: () => setShowDefaultManager(true) }, t("h_cj_default_add_supplier", "Add Supplier")),
        h(Button, { variant: "secondary", onClick: syncAllSuppliers }, t("h_cj_sync_all_suppliers", "Sync all CJ suppliers"))
      ),
    }),
    h("form", { className: "cjds-toolbar", onSubmit: (event) => { event.preventDefault(); setQuery(keyword.trim()); } },
      h("input", {
        className: "cjds-input",
        value: keyword,
        onChange: (event) => setKeyword(event.target.value),
        placeholder: t("h_cj_supplier_search_placeholder", "Search suppliers by name"),
        "aria-label": t("h_cj_supplier_search", "Search suppliers"),
      }),
      h(Button, { type: "submit" }, t("h_cj_search", "Search")),
      h(Button, { variant: "secondary", disabled: !keyword && !query, onClick: () => { setKeyword(""); setQuery(""); } }, t("h_cj_reset", "Reset"))
    ),
    h("section", { className: "cjds-default-section cjds-personal-suppliers" },
      h("h3", null, t("h_cj_my_suppliers", "My Suppliers")),
      h(ResourceState, {
        loading: personalResource.loading && !personalResource.data,
        error: personalResource.error,
        empty: !personalSuppliers.length,
        onRetry: personalResource.reload,
        emptyTitle: t("h_cj_my_suppliers_empty", "No personal suppliers yet"),
        emptyDescription: t("h_cj_my_suppliers_empty_desc", "Create a supplier to organize CJ products without changing their official CJ supplier identity."),
        t,
      }, h("div", { className: "cjds-grid" }, ...personalSuppliers.map((supplier) => h("article", { className: "cjds-card", key: supplier._id },
        h("div", { className: "cjds-card-body" },
          h("div", { className: "cjds-row cjds-between" }, h("h3", null, supplier.name), supplier.includeInDefaultLoading ? h("span", { className: "cjds-badge" }, t("h_cj_default_set", "Default")) : null),
          h("p", { className: "cjds-muted" }, `${(supplier.products || []).length} ${t("h_cj_products_count", "Products")}`),
          supplier.cjSupplierId ? h("p", { className: "cjds-muted" }, `${t("h_cj_default_cj_supplier_id_label", "CJ Supplier ID")}: ${supplier.cjSupplierId}`) : null,
          h(Button, { variant: "secondary", onClick: () => setShowDefaultManager(true) }, t("h_cj_default_edit", "Edit"))
        )
      ))))
    ),
    h("h3", { className: "cjds-section-title" }, t("h_cj_official_suppliers", "CJ Suppliers")),
    // Suppliers have no warehouse-origin field of their own -- resolved
    // server-side via the suppliers that have at least one cached product
    // matching this fromCountryCode (see cjSupplierService.fetchSuppliers).
    // Same controlled component/pattern as ProductCollectionPage.js.
    h(ShipFromFilter, {
      value: fromCountryCode,
      onChange: setFromCountryCode,
    }),
    message ? h("div", { className: "cjds-success" }, message) : null,
    mutationError ? h("div", { className: "cjds-alert", role: "alert" }, mutationError) : null,
    // Ne bloque tout l'écran derrière "Loading…" que pour le tout premier
    // chargement -- sinon la grille ET la PaginationBar se démontent à
    // chaque clic Suivant/Précédent (loading repasse à true), ce qui fait
    // disparaître/réapparaître la pagination et se lit comme un bug. Voir
    // ProductCollectionPage.js pour le même correctif.
    h(ResourceState, {
      loading: resource.loading && !resource.data,
      error: resource.error,
      empty: !suppliers.length,
      onRetry: resource.reload,
      t,
      // Ship From n'a de sens ici que pour les suppliers ayant déjà des
      // produits en cache avec un fromCountryCode réel (voir ShipFromFilter.js
      // et ProductCollectionPage.js pour la même limite côté produits) --
      // évite de lire un résultat vide comme "le filtre est cassé".
      ...(fromCountryCode && !suppliers.length ? {
        emptyTitle: t("h_cj_supplier_shipfrom_empty_title", "No suppliers with cached products from this country yet"),
        emptyDescription: t(
          "h_cj_supplier_shipfrom_empty_desc",
          "This matches suppliers whose already-synced products carry this Ship From country -- open more products from this supplier or switch back to \"All\" to see the full list."
        ),
      } : {}),
    },
      h("div", { className: "cjds-grid" }, ...suppliers.map((supplier) => h(SupplierCard, {
        key: supplier._id,
        supplier,
        t,
        busy: busySupplierId === supplier._id,
        onView: (item) => setViewSupplierId(item._id),
        onSync: syncOne,
      }))),
      h(PaginationBar, {
        page, pageCount, loading: resource.loading, t,
        onPrevious: () => setPage((current) => Math.max(1, current - 1)),
        onNext: () => setPage((current) => Math.min(pageCount, current + 1)),
      })
    ),
    h(CJDropshippingViewModal, {
      show: Boolean(viewSupplierId),
      onHide: () => setViewSupplierId(null),
      kind: "supplier",
      id: viewSupplierId,
      t,
    }),
    h(DefaultLoadingManager, {
      show: showDefaultManager,
      onHide: () => setShowDefaultManager(false),
      onChanged: personalResource.reload,
    })
  );
};

module.exports = { DropshippingSuppliers };
